import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Target, 
  TrendingUp, 
  Calendar, 
  Euro, 
  Calculator,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
  Save,
  Trash2,
  X
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface SavingsGoalCalculatorProps {
  currentSavings: number;
  averageSkippedPerMonth: number;
}

interface SavingsGoal {
  id?: string;
  name: string;
  targetAmount: number;
  deadline?: string;
}

interface DatabaseSavingsGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  deadline?: string;
  created_at: string;
  updated_at: string;
}

const PRESET_GOALS = [
  { name: 'Notgroschen (1.000€)', targetAmount: 1000 },
  { name: 'Urlaub (2.500€)', targetAmount: 2500 },
  { name: 'Laptop (1.500€)', targetAmount: 1500 },
  { name: 'Auto-Anzahlung (5.000€)', targetAmount: 5000 },
  { name: 'Eigenheim-Anzahlung (20.000€)', targetAmount: 20000 },
];

export function SavingsGoalCalculator({ currentSavings, averageSkippedPerMonth }: SavingsGoalCalculatorProps) {
  // Fallback für ungültige Props
  const safeSavings = isNaN(currentSavings) ? 0 : currentSavings;
  const safeAverage = isNaN(averageSkippedPerMonth) ? 0 : averageSkippedPerMonth;
  
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<SavingsGoal | null>(null);
  const [customGoal, setCustomGoal] = useState({ name: '', targetAmount: 0, deadline: '' });
  const [showCustom, setShowCustom] = useState(false);
  const [savedGoals, setSavedGoals] = useState<SavingsGoal[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Einfache Fallback-Darstellung für Debugging
  if (!user) {
    return (
      <Card className="bg-white border border-gray-100 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Target className="w-4 h-4 text-emerald-600" />
            <div>
              <h3 className="font-medium text-gray-900 text-sm">Sparziel-Rechner</h3>
              <p className="text-xs text-gray-500">Benutzer nicht angemeldet</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Lade gespeicherte Daten beim Mount und bei User-Änderungen
  useEffect(() => {
    if (user && !initialLoadComplete) {
      loadSelectedGoal();
      setInitialLoadComplete(true);
    }
  }, [user, initialLoadComplete]);

  // Lade gespeicherte Sparziele wenn expandiert wird
  useEffect(() => {
    if (user && isExpanded && savedGoals.length === 0) {
      fetchSavedGoals();
    }
  }, [isExpanded, user]);

  const fetchSavedGoals = async () => {
    try {
      console.log('Fetching saved goals for user:', user?.id);
      const { data, error } = await supabase
        .from('user_savings_goals')
        .select('*')
        .eq('user_id', user?.id)
        .not('name', 'like', '__SELECTED_GOAL__%')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Fetched goals data:', data);
      const goals = data?.map((goal: DatabaseSavingsGoal) => ({
        id: goal.id,
        name: goal.name,
        targetAmount: goal.target_amount,
        deadline: goal.deadline
      })) || [];

      setSavedGoals(goals);
      console.log('Mapped goals:', goals);
    } catch (error) {
      console.error('Error fetching saved goals:', error);
      toast.error('Fehler beim Laden der Sparziele');
    }
  };

  const loadSelectedGoal = async () => {
    try {
      console.log('Loading selected goal for user:', user?.id);
      
      // Versuche aus einer einfachen Metadaten-Tabelle zu laden
      try {
        const { data, error } = await supabase
          .from('user_savings_goals')
          .select('*')
          .eq('user_id', user?.id)
          .like('name', '__SELECTED_GOAL__%')
          .maybeSingle();

        if (!error && data && data.name.startsWith('__SELECTED_GOAL__')) {
          // Dekodiere die gespeicherten Daten aus dem name-Feld
          const jsonData = data.name.replace('__SELECTED_GOAL__', '');
          try {
            const goalData = JSON.parse(jsonData);
            if (goalData.name) {
              setSelectedGoal(goalData);
              localStorage.setItem(`selectedGoal_${user?.id}`, JSON.stringify(goalData));
              console.log('✅ Loaded goal from database metadata');
              return;
            }
          } catch (parseError) {
            console.log('Failed to parse goal data:', parseError);
          }
        }
      } catch (dbError) {
        console.log('Database metadata load failed:', dbError);
      }
      
      // Fallback: localStorage verwenden
      const savedGoalData = localStorage.getItem(`selectedGoal_${user?.id}`);
      if (savedGoalData) {
        try {
          const parsedGoal = JSON.parse(savedGoalData);
          console.log('✅ Loaded goal from localStorage (fallback):', parsedGoal);
          setSelectedGoal(parsedGoal);
          // Versuche es auch in der Datenbank zu speichern
          await saveSelectedGoal(parsedGoal);
        } catch (parseError) {
          console.error('Error parsing saved goal data:', parseError);
          localStorage.removeItem(`selectedGoal_${user?.id}`);
        }
      }
    } catch (error) {
      console.error('Error loading selected goal:', error);
    }
  };

  const saveSelectedGoal = async (goal: SavingsGoal | null) => {
    try {
      console.log('Saving selected goal:', goal);
      
      // Speichere in localStorage (funktioniert immer)
      if (goal) {
        localStorage.setItem(`selectedGoal_${user?.id}`, JSON.stringify(goal));
      } else {
        localStorage.removeItem(`selectedGoal_${user?.id}`);
      }
      
      // Versuche auch in Datenbank zu speichern (für browserübergreifende Persistierung)
      try {
        // Lösche zuerst alle bestehenden Metadaten-Einträge
        await supabase
          .from('user_savings_goals')
          .delete()
          .eq('user_id', user?.id)
          .like('name', '__SELECTED_GOAL__%');
        
        if (goal) {
          // Füge neuen Metadaten-Eintrag hinzu
          const { error } = await supabase
            .from('user_savings_goals')
            .insert({
              user_id: user?.id,
              name: `__SELECTED_GOAL__${JSON.stringify(goal)}`, // Speichere die Daten im name-Feld
              target_amount: 0, // Dummy-Wert
              deadline: null // Kein Datum
            });

          if (!error) {
            console.log('✅ Selected goal saved to database metadata');
        } else {
            console.log('Database save failed:', error.message);
          }
        } else {
          console.log('✅ Selected goal removed from database');
        }
      } catch (dbError) {
        console.log('Database operation failed, using localStorage only:', dbError);
      }
    } catch (error) {
      console.error('❌ Error saving selected goal:', error);
      toast.error('Fehler beim Speichern der Auswahl');
    }
  };

  const saveCustomGoal = async () => {
    if (!customGoal.name || !customGoal.targetAmount) {
      toast.error('Bitte fülle alle Felder aus');
      return;
    }

    if (!user?.id) {
      toast.error('Benutzer nicht angemeldet');
      return;
    }

    setLoading(true);
    try {
      console.log('Saving goal:', {
        user_id: user.id,
        name: customGoal.name,
        target_amount: customGoal.targetAmount,
        deadline: customGoal.deadline || null
      });

      const { data, error } = await supabase
        .from('user_savings_goals')
        .insert({
          user_id: user.id,
          name: customGoal.name,
          target_amount: customGoal.targetAmount,
          deadline: customGoal.deadline || null
        })
        .select()
        .maybeSingle();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Saved goal data:', data);

      const newGoal: SavingsGoal = {
        id: data.id,
        name: data.name,
        targetAmount: data.target_amount,
        deadline: data.deadline
      };

      setSavedGoals([newGoal, ...savedGoals]);
      setSelectedGoal(newGoal);
      await saveSelectedGoal(newGoal);
      setCustomGoal({ name: '', targetAmount: 0, deadline: '' });
      setShowCustom(false);
      toast.success('Sparziel erfolgreich gespeichert und ausgewählt!');
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error(`Fehler beim Speichern des Sparziels: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteGoal = async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('user_savings_goals')
        .delete()
        .eq('id', goalId)
        .eq('user_id', user?.id);

      if (error) throw error;

      setSavedGoals(savedGoals.filter(goal => goal.id !== goalId));
      if (selectedGoal?.id === goalId) {
        setSelectedGoal(null);
        await saveSelectedGoal(null);
      }
      toast.success('Sparziel gelöscht');
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Fehler beim Löschen des Sparziels');
    }
  };

  const deselectGoal = async () => {
    setSelectedGoal(null);
    await saveSelectedGoal(null);
    toast.success('Sparziel deselektiert');
  };

  const selectGoal = async (goal: SavingsGoal) => {
    console.log('🎯 Selecting goal:', goal);
    setSelectedGoal(goal);
    await saveSelectedGoal(goal);
    toast.success(`Sparziel "${goal.name}" ausgewählt!`);
  };

  const calculateTimeToGoal = (targetAmount: number) => {
    const remainingAmount = Math.max(0, targetAmount - safeSavings);
    if (safeAverage === 0) return null;
    
    const estimatedMonthlySavings = safeAverage * 50;
    if (estimatedMonthlySavings === 0) return null;
    
    return Math.ceil(remainingAmount / estimatedMonthlySavings);
  };

  const formatTimeToGoal = (months: number | null) => {
    if (months === null) return 'Nicht berechenbar';
    if (months === 0) return 'Ziel bereits erreicht! 🎉';
    if (months === 1) return '1 Monat';
    if (months < 12) return `${months} Monate`;
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (remainingMonths === 0) return `${years} Jahr${years > 1 ? 'e' : ''}`;
    return `${years} Jahr${years > 1 ? 'e' : ''} und ${remainingMonths} Monat${remainingMonths > 1 ? 'e' : ''}`;
  };

  const getProgressPercentage = (targetAmount: number) => {
    return Math.min(100, (safeSavings / targetAmount) * 100);
  };

  const handleCustomGoalSubmit = async () => {
    if (customGoal.name && customGoal.targetAmount > 0) {
      try {
        const newGoal = {
          name: customGoal.name,
          targetAmount: customGoal.targetAmount,
          deadline: customGoal.deadline
        };
        await selectGoal(newGoal);
        setCustomGoal({ name: '', targetAmount: 0, deadline: '' });
        setShowCustom(false);
      } catch (error) {
        console.error('Error submitting custom goal:', error);
      }
    }
  };

  // Minimalistisches Design
  if (!isExpanded) {
    return (
      <Card className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 mb-6 cursor-pointer">
        <CardContent 
          className="p-6"
          onClick={() => setIsExpanded(true)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center group-hover:from-emerald-100 group-hover:to-emerald-200 transition-all duration-300">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="space-y-1">
                <h3 className="font-medium text-gray-900">Sparziel-Rechner</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-emerald-600">{Math.round(safeSavings)}€</span> gespart
                  {selectedGoal && (
                    <span className="ml-2 text-gray-400">
                      → <span className="text-gray-600 font-medium">{selectedGoal.name}</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedGoal && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    deselectGoal();
                  }}
                  className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-xl group/btn"
                  title="Sparziel deselektieren"
                >
                  <X className="w-4 h-4 transition-transform duration-200 group-hover/btn:scale-110" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 h-8 w-8 p-0 rounded-xl group/btn"
              >
                <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover/btn:scale-110" />
              </Button>
            </div>
          </div>
          
          {selectedGoal && (
            <div className="mt-4 space-y-3">
              <Progress 
                value={getProgressPercentage(selectedGoal.targetAmount)} 
                className="h-2 progress-bar"
              />
              <div className="flex justify-between items-center text-sm">
                <span className="text-emerald-600 font-medium">
                  {getProgressPercentage(selectedGoal.targetAmount).toFixed(0)}% erreicht
                </span>
                <span className="text-gray-500">
                  {formatTimeToGoal(calculateTimeToGoal(selectedGoal.targetAmount))}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 mb-6">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
              <Target className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <CardTitle className="text-xl font-light text-gray-900">Sparziel-Rechner</CardTitle>
              <CardDescription className="text-gray-600 mt-1">
                Berechne deine Sparziele mit KaufPause
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedGoal && (
              <Button
                variant="ghost"
                size="sm"
                onClick={deselectGoal}
                className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-xl group"
                title="Sparziel deselektieren"
              >
                <X className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-50 h-8 w-8 p-0 rounded-xl group"
            >
              <ChevronUp className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Aktuelle Statistiken */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="group bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-emerald-700 font-medium">Bereits gespart</p>
                  <p className="text-3xl font-light text-emerald-900">{Math.round(safeSavings)}€</p>
                  <p className="text-xs text-emerald-600">Dein Erfolg</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-300">
                  <Euro className="w-6 h-6 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">Ø Impulskäufe vermieden</p>
                  <p className="text-3xl font-light text-gray-900">{safeAverage}/Monat</p>
                  <p className="text-xs text-gray-500">Durchschnitt</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">Geschätzte Ersparnis</p>
                  <p className="text-3xl font-light text-gray-900">{safeAverage * 50}€</p>
                  <p className="text-xs text-gray-500">pro Monat</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition-colors duration-300">
                  <Calculator className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sparziel-Auswahl */}
        <div>
          <h3 className="text-lg font-medium mb-4 text-gray-900">Wähle dein Sparziel</h3>
          
          {/* Gespeicherte Sparziele */}
          {savedGoals.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-emerald-700 mb-3">Deine gespeicherten Sparziele</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {savedGoals.map((goal) => (
                  <div key={goal.id} className="flex items-center gap-2">
                    <Button
                      variant={selectedGoal?.id === goal.id ? "default" : "outline"}
                      className={`flex-1 h-auto p-4 text-left justify-start rounded-xl transition-all duration-200 ${
                        selectedGoal?.id === goal.id 
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' 
                          : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                      onClick={() => selectGoal(goal)}
                    >
                      <div>
                        <div className="font-medium">{goal.name}</div>
                        <div className={`text-sm ${selectedGoal?.id === goal.id ? 'text-emerald-100' : 'text-gray-500'}`}>
                          {goal.targetAmount.toLocaleString()}€
                        </div>
                      </div>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => goal.id && deleteGoal(goal.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0 h-10 w-10 rounded-xl"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Vordefinierte Sparziele */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {PRESET_GOALS.map((goal) => (
              <Button
                key={goal.name}
                variant={selectedGoal?.name === goal.name && !selectedGoal?.id ? "default" : "outline"}
                className={`h-auto p-4 text-left justify-start rounded-xl transition-all duration-200 ${
                  selectedGoal?.name === goal.name && !selectedGoal?.id
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                }`}
                onClick={() => selectGoal(goal)}
              >
                <div>
                  <div className="font-medium">{goal.name}</div>
                  <div className={`text-sm ${
                    selectedGoal?.name === goal.name && !selectedGoal?.id ? 'text-emerald-100' : 'text-gray-500'
                  }`}>
                    {goal.targetAmount.toLocaleString()}€
                  </div>
                </div>
              </Button>
            ))}
          </div>

          <Button
            variant="outline"
            onClick={() => setShowCustom(!showCustom)}
            className="w-full rounded-xl border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-200"
          >
            <Target className="w-4 h-4 mr-2" />
            Eigenes Sparziel definieren
          </Button>

          {showCustom && (
            <Card className="mt-4 bg-gray-50/50 border-gray-200/60">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="goalName" className="text-sm font-medium text-gray-700">Ziel-Name</Label>
                    <Input
                      id="goalName"
                      placeholder="z.B. Traumurlaub"
                      value={customGoal.name}
                      onChange={(e) => setCustomGoal({ ...customGoal, name: e.target.value })}
                      className="mt-1 rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200/50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="goalAmount" className="text-sm font-medium text-gray-700">Zielbetrag (€)</Label>
                    <Input
                      id="goalAmount"
                      type="number"
                      placeholder="z.B. 3000"
                      value={customGoal.targetAmount || ''}
                      onChange={(e) => setCustomGoal({ ...customGoal, targetAmount: Number(e.target.value) })}
                      className="mt-1 rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200/50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="goalDeadline" className="text-sm font-medium text-gray-700">Zieldatum (optional)</Label>
                    <Input
                      id="goalDeadline"
                      type="date"
                      value={customGoal.deadline}
                      onChange={(e) => setCustomGoal({ ...customGoal, deadline: e.target.value })}
                      className="mt-1 rounded-xl border-gray-200 focus:border-emerald-300 focus:ring-emerald-200/50"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button 
                    onClick={handleCustomGoalSubmit} 
                    variant="outline" 
                    className="flex-1 rounded-xl border-gray-200 hover:border-emerald-300 hover:bg-emerald-50"
                  >
                    Temporär setzen
                  </Button>
                  <Button 
                    onClick={saveCustomGoal} 
                    disabled={loading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Speichern...' : 'Speichern'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sparziel-Analyse */}
        {selectedGoal && (
          <Card className="bg-gradient-to-br from-gray-50/50 to-white border border-gray-200/60 animate-in fade-in-50 duration-300">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-lg font-medium">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Target className="w-4 h-4 text-emerald-600" />
                </div>
                Dein Sparziel: {selectedGoal.name}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={deselectGoal}
                  className="ml-auto text-gray-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0 rounded-xl"
                  title="Sparziel deselektieren"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-0">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Fortschritt</span>
                <span className="text-sm text-gray-500">
                  {Math.round(safeSavings)}€ von {selectedGoal.targetAmount.toLocaleString()}€
                </span>
              </div>
              
              <Progress 
                value={getProgressPercentage(selectedGoal.targetAmount)} 
                className="h-3 progress-bar"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Noch benötigt</p>
                    <p className="text-xl font-light text-emerald-700">
                      {Math.max(0, selectedGoal.targetAmount - safeSavings).toLocaleString()}€
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Geschätzte Zeit</p>
                    <p className="text-xl font-light text-blue-700">
                      {formatTimeToGoal(calculateTimeToGoal(selectedGoal.targetAmount))}
                    </p>
                  </div>
                </div>
              </div>

              {selectedGoal.deadline && (
                <div className="bg-blue-50/50 border border-blue-200/60 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-blue-900">
                      Zieldatum: {new Date(selectedGoal.deadline).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
} 